import { useState } from 'react'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Alert,
  Spin,
  Typography,
} from 'antd'
import {
  LinkOutlined,
  DisconnectOutlined,
  SyncOutlined,
  ExportOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { auth0Api, type CreateAuth0UserRequest } from '@/api/auth0'
import { usersApi } from '@/api/users'
import { useAuth } from '@/hooks/useAuth'
import { getApiErrorMessage } from '@/utils/error'

const { Text, Paragraph } = Typography

interface Auth0IntegrationProps {
  userId: string
  userEmail?: string
}

interface EmailConfirmationModalProps {
  open: boolean
  title: string
  email: string
  warningMessage: string
  onConfirm: () => void
  onCancel: () => void
  loading?: boolean
}

const EmailConfirmationModal = ({
  open,
  title,
  email,
  warningMessage,
  onConfirm,
  onCancel,
  loading,
}: EmailConfirmationModalProps) => {
  const [confirmEmail, setConfirmEmail] = useState('')
  const isValid = confirmEmail === email

  const handleConfirm = () => {
    if (isValid) {
      onConfirm()
      setConfirmEmail('')
    }
  }

  const handleCancel = () => {
    onCancel()
    setConfirmEmail('')
  }

  return (
    <Modal
      title={
        <Space>
          <WarningOutlined style={{ color: '#ff4d4f' }} />
          {title}
        </Space>
      }
      open={open}
      onOk={handleConfirm}
      onCancel={handleCancel}
      okText="Confirm"
      okButtonProps={{ danger: true, disabled: !isValid }}
      confirmLoading={loading}
      width={600}
    >
      <Alert
        message="Critical Operation"
        description={warningMessage}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Paragraph>
        To confirm this action, please type the user's email address:{' '}
        <Text strong code>
          {email}
        </Text>
      </Paragraph>
      <Input
        placeholder="Type the email address to confirm"
        value={confirmEmail}
        onChange={(e) => setConfirmEmail(e.target.value)}
        status={confirmEmail && !isValid ? 'error' : undefined}
        autoFocus
      />
      {confirmEmail && !isValid && (
        <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
          Email address does not match
        </Text>
      )}
    </Modal>
  )
}

export const Auth0Integration = ({ userId, userEmail: propUserEmail }: Auth0IntegrationProps) => {
  const { user: currentUser } = useAuth()
  const isAdmin = currentUser?.role === 'ADMIN'

  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [linkConfirmOpen, setLinkConfirmOpen] = useState(false)
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false)
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false)

  const [linkForm] = Form.useForm()
  const [createForm] = Form.useForm()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['auth0-user', userId],
    queryFn: async () => {
      const response = await auth0Api.getAuth0User(userId)
      return response.data.data
    },
  })

  const { data: userData } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      const response = await usersApi.getUser(userId)
      return response.data
    },
    enabled: !propUserEmail, // Only fetch if email not provided as prop
  })

  const linkMutation = useMutation({
    mutationFn: async (values: { auth0UserId: string; createIfNotExists: boolean }) => {
      return auth0Api.linkToAuth0(userId, values)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth0-user', userId] })
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      message.success('Successfully linked to Auth0')
      setLinkModalOpen(false)
      setLinkConfirmOpen(false)
      linkForm.resetFields()
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to link to Auth0'))
    },
  })

  const createMutation = useMutation({
    mutationFn: async (values: CreateAuth0UserRequest) => {
      return auth0Api.createAuth0User(userId, values)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth0-user', userId] })
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      message.success('Successfully created Auth0 user and linked account')
      setCreateModalOpen(false)
      setCreateConfirmOpen(false)
      createForm.resetFields()
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to create Auth0 user'))
    },
  })

  const unlinkMutation = useMutation({
    mutationFn: async () => {
      return auth0Api.unlinkFromAuth0(userId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth0-user', userId] })
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      message.success('Successfully unlinked from Auth0')
      setUnlinkConfirmOpen(false)
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to unlink from Auth0'))
    },
  })

  const syncMutation = useMutation({
    mutationFn: async () => {
      return auth0Api.syncWithAuth0(userId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth0-user', userId] })
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      message.success('Successfully synced with Auth0')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to sync with Auth0'))
    },
  })

  const handleLinkFormSubmit = async () => {
    try {
      await linkForm.validateFields()
      setLinkModalOpen(false)
      setLinkConfirmOpen(true)
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  const handleLinkConfirm = async () => {
    const values = linkForm.getFieldsValue()
    linkMutation.mutate(values)
  }

  const handleCreateFormSubmit = async () => {
    try {
      await createForm.validateFields()
      setCreateModalOpen(false)
      setCreateConfirmOpen(true)
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  const handleCreateConfirm = async () => {
    const values = createForm.getFieldsValue()
    createMutation.mutate(values)
  }

  const handleViewInAuth0 = () => {
    if (data?.adminUrl) {
      window.open(data.adminUrl, '_blank')
    }
  }

  if (isLoading) {
    return (
      <Card title="Auth0 Integration">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      </Card>
    )
  }

  // Check if Auth0 is configured
  if (data?.error?.includes('not configured')) {
    return (
      <Card title="Auth0 Integration">
        <Alert
          message="Auth0 Not Configured"
          description="Auth0 integration is not configured on the server. Please contact your administrator to set up Auth0."
          type="warning"
          showIcon
        />
      </Card>
    )
  }

  const isLinked = data?.linked || false
  const isStale = data?.stale || false
  const auth0User = data?.auth0User
  const userEmail = propUserEmail || userData?.email || ''

  return (
    <Card title="Auth0 Integration">
      {!isAdmin && (
        <Alert
          message="Admin Access Required"
          description="Only administrators can create, link, or unlink Auth0 accounts. This is a critical security operation."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {!isLinked ? (
        <>
          <Alert
            message="Not linked to Auth0"
            description="This user is not connected to an Auth0 account. You can link to an existing Auth0 account or create a new one."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalOpen(true)}
              disabled={!isAdmin}
            >
              Create Auth0 User
            </Button>
            <Button
              icon={<LinkOutlined />}
              onClick={() => setLinkModalOpen(true)}
              disabled={!isAdmin}
            >
              Link to Existing
            </Button>
          </Space>
        </>
      ) : (
        <>
          {isStale && (
            <Alert
              message="Stale Link"
              description={
                data?.error || 'The Auth0 account no longer exists. The link may be stale.'
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          {auth0User && (
            <>
              <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Status" span={2}>
                  <Tag
                    color={isStale ? 'warning' : 'success'}
                    icon={isStale ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                  >
                    {isStale ? 'Stale Link' : 'Linked'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Auth0 User ID" span={2}>
                  <code style={{ fontSize: '11px' }}>{auth0User.user_id}</code>
                </Descriptions.Item>
                <Descriptions.Item label="Email">{auth0User.email}</Descriptions.Item>
                <Descriptions.Item label="Email Verified">
                  <Tag color={auth0User.email_verified ? 'success' : 'default'}>
                    {auth0User.email_verified ? 'Yes' : 'No'}
                  </Tag>
                </Descriptions.Item>
                {auth0User.name && (
                  <Descriptions.Item label="Name" span={2}>
                    {auth0User.name}
                  </Descriptions.Item>
                )}
                {auth0User.blocked !== undefined && (
                  <Descriptions.Item label="Account Status">
                    <Tag color={auth0User.blocked ? 'error' : 'success'}>
                      {auth0User.blocked ? 'Blocked' : 'Active'}
                    </Tag>
                  </Descriptions.Item>
                )}
                {auth0User.logins_count !== undefined && (
                  <Descriptions.Item label="Login Count">
                    {auth0User.logins_count}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Created At">
                  {new Date(auth0User.created_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Updated At">
                  {new Date(auth0User.updated_at).toLocaleString()}
                </Descriptions.Item>
                {auth0User.last_login && (
                  <Descriptions.Item label="Last Login" span={2}>
                    {new Date(auth0User.last_login).toLocaleString()}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </>
          )}
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleViewInAuth0}
              disabled={isStale || !data?.adminUrl}
            >
              View in Auth0
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={() => syncMutation.mutate()}
              loading={syncMutation.isPending}
              disabled={isStale}
            >
              Sync with Auth0
            </Button>
            <Button
              danger
              icon={<DisconnectOutlined />}
              onClick={() => setUnlinkConfirmOpen(true)}
              loading={unlinkMutation.isPending}
              disabled={!isAdmin}
            >
              Unlink
            </Button>
          </Space>
        </>
      )}

      {/* Link to Existing Auth0 User Modal */}
      <Modal
        title="Link to Existing Auth0 User"
        open={linkModalOpen}
        onOk={handleLinkFormSubmit}
        onCancel={() => {
          setLinkModalOpen(false)
          linkForm.resetFields()
        }}
        okText="Next"
      >
        <Form form={linkForm} layout="vertical" initialValues={{ createIfNotExists: false }}>
          <Form.Item
            name="auth0UserId"
            label="Auth0 User ID"
            rules={[{ required: true, message: 'Auth0 User ID is required' }]}
          >
            <Input placeholder="e.g., auth0|64a1b2c3d4e5f6g7h8i9j0k1" />
          </Form.Item>
          <Form.Item name="createIfNotExists" valuePropName="checked" hidden>
            <Input type="checkbox" />
          </Form.Item>
          <Alert
            message="Critical Operation"
            description="Linking an Auth0 account will associate this user's authentication with the specified Auth0 account. This operation is audited and requires confirmation."
            type="warning"
            showIcon
          />
        </Form>
      </Modal>

      {/* Create New Auth0 User Modal */}
      <Modal
        title="Create New Auth0 User"
        open={createModalOpen}
        onOk={handleCreateFormSubmit}
        onCancel={() => {
          setCreateModalOpen(false)
          createForm.resetFields()
        }}
        okText="Next"
        width={600}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            tooltip="Leave empty to use the user's X-Manager email"
          >
            <Input placeholder="Uses user's current email if not provided" />
          </Form.Item>
          <Form.Item name="password" label="Password" tooltip="Optional initial password">
            <Input.Password placeholder="Auto-generated if not provided" />
          </Form.Item>
          <Form.Item
            name="given_name"
            label="Given Name"
            tooltip="Leave empty to use user's first name"
          >
            <Input placeholder="Uses user's first name if not provided" />
          </Form.Item>
          <Form.Item
            name="family_name"
            label="Family Name"
            tooltip="Leave empty to use user's last name"
          >
            <Input placeholder="Uses user's last name if not provided" />
          </Form.Item>
          <Alert
            message="Critical Operation"
            description="Creating an Auth0 user will create a new authentication account and link it to this X-Manager user. This operation is audited and requires confirmation."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Form>
      </Modal>

      {/* Email Confirmation Modals */}
      <EmailConfirmationModal
        open={linkConfirmOpen}
        title="Confirm Link to Auth0"
        email={userEmail}
        warningMessage="You are about to link this X-Manager user to an existing Auth0 account. This will associate their authentication with the specified Auth0 account. This action is audited and cannot be easily reversed."
        onConfirm={handleLinkConfirm}
        onCancel={() => setLinkConfirmOpen(false)}
        loading={linkMutation.isPending}
      />

      <EmailConfirmationModal
        open={createConfirmOpen}
        title="Confirm Create Auth0 User"
        email={userEmail}
        warningMessage="You are about to create a new Auth0 authentication account and link it to this X-Manager user. This will create a new login credential for the user. This action is audited and cannot be easily reversed."
        onConfirm={handleCreateConfirm}
        onCancel={() => setCreateConfirmOpen(false)}
        loading={createMutation.isPending}
      />

      <EmailConfirmationModal
        open={unlinkConfirmOpen}
        title="Confirm Unlink from Auth0"
        email={userEmail}
        warningMessage="You are about to unlink this user from their Auth0 account. This will remove the authentication association but will NOT delete the Auth0 account. The user may lose access to the Xferall application. This action is audited and cannot be easily reversed."
        onConfirm={() => unlinkMutation.mutate()}
        onCancel={() => setUnlinkConfirmOpen(false)}
        loading={unlinkMutation.isPending}
      />
    </Card>
  )
}
